import { useMemo, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@clerk/react";
import {
  CheckCircle2Icon,
  MessageCircleQuestionIcon,
  TriangleAlertIcon,
  UndoIcon,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Frame,
  FrameFooter,
  FrameHeader,
  FramePanel,
  FrameTitle,
} from "@/components/ui/frame";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { BadgeStatus } from "@/components/dashboard_campaigns/status-badges";
import { useCreatorsStore, useEveActionsStore } from "@/stores";
import { useCreatorsTableContext, useCreatorsTableState } from "../CreatorsTableContext";
import {
  customStatusKind,
  evePhaseKeyForStatus,
  evePayoutState,
  isEvePayoutSlotKey,
  pipelineForCreator,
  resolveEvePhaseSettings,
} from "@/lib/eve";
import { useAmyDeepLink } from "@/hooks/use-amy-deep-link";
import {
  ArtifactThreadItem,
  ContractBlock,
  InfoRow,
  PaymentBlock,
  fmtDate,
  phaseDisplayLabel,
} from "./blocks";
import PayoutDecision from "./PayoutDecision";
import { payoutPrepared } from "./phaseState";

/**
 * The detail + decision dialog behind every phase cell — mounted ONCE in
 * CreatorsTable (the ActivateCreatorDialog pattern) and driven by the
 * context's phaseDialog state. Same section order for EVERY phase:
 * Summary → phase facts (contract / payout / hold) → Submissions →
 * Tool runs → the action frame (verdict / payout decision) → Discuss link.
 * Verdicts and payout confirm/cancel execute NATIVELY against the amy
 * action endpoints; gate releases stay chat.
 */

/** True when a prepared, unexpired payout preview is on file (confirm-able). */
export default function PhaseDetailDialog() {
  const { getToken } = useAuth();
  const { setPhaseDialog, onRefresh, campaignFor } = useCreatorsTableContext();
  const { phaseDialog } = useCreatorsTableState();
  const { creators } = useCreatorsStore();
  const { resolvePhaseReview, confirmPayout, cancelPayout, isLoading } = useEveActionsStore();
  const openAmy = useAmyDeepLink();

  const [notes, setNotes] = useState("");

  const creator = creators.find((c) => c._id === phaseDialog?.creatorId);
  // The creator's own campaign (pipeline labels, settings) — org-wide the
  // one mounted dialog serves rows from many campaigns.
  const campaign = campaignFor(creator);
  // The pipeline THIS creator runs — their closing tier's: entries, labels
  // and settings differ per tier.
  const pipeline = pipelineForCreator(campaign, creator);
  const phaseKey = phaseDialog?.phaseKey ?? null;
  const phase = creator?.eve_phase;
  const entry = pipeline.find((p) => p.key === phaseKey);
  const label = phaseDisplayLabel(pipeline, phaseKey);
  const status = creator?.status ?? "";

  const currentKey = evePhaseKeyForStatus(status);
  const isCurrent = !!phaseKey && currentKey === phaseKey;
  const completedEntry = phase?.history?.find((h) => h.key === phaseKey);
  const completedAt =
    completedEntry?.completed_at ?? (phaseKey === "contract" ? phase?.contract?.completed_at : undefined);
  const isCompleted = !isCurrent && (!!completedEntry || status === "completed" || !!completedAt);

  // Which payout slot (payment_1/payment_2/payment) this dialog shows, if any,
  // and that slot's own state subdoc.
  const isPayoutPhase = !!phaseKey && isEvePayoutSlotKey(phaseKey);
  const payoutState = isPayoutPhase ? evePayoutState(phase, phaseKey!) : undefined;

  // Shape of THIS phase (drives which summary rows apply).
  const shape =
    phaseKey === "contract" || isPayoutPhase
      ? "gate"
      : (entry?.shape ?? "review"); // built-ins script/draft/posting + custom default = review
  const settings = entry ? resolveEvePhaseSettings(entry) : null;

  const verdictMode = isCurrent && status.endsWith("-review");
  const payoutMode = isCurrent && isPayoutPhase && status.startsWith("pending-") && payoutPrepared(payoutState);
  const waitMode = isCurrent && customStatusKind(status) === "wait";

  const artifacts = useMemo(() => {
    const all = [...(phase?.artifacts ?? [])].reverse();
    const sliced = phaseKey ? all.filter((a) => a.kind === phaseKey) : all;
    return sliced.length > 0 ? sliced : phaseKey === "contract" || isPayoutPhase ? [] : all;
  }, [phase?.artifacts, phaseKey, isPayoutPhase]);
  const checks = (phase?.checks ?? []).filter((c) => c.phase_key === phaseKey);
  const gate = phase?.gate;

  const close = () => {
    setPhaseDialog(null);
    setNotes("");
  };

  const runVerdict = async (decision: "approve" | "revision") => {
    if (!creator) return;
    const token = await getToken();
    if (!token) return;
    const ok = await resolvePhaseReview(creator._id, decision, notes, token);
    if (ok) {
      close();
      await onRefresh();
    }
  };

  const runConfirmPayout = async () => {
    if (!creator) return false;
    const token = await getToken();
    if (!token) return false;
    const ok = await confirmPayout(creator._id, token);
    if (ok) {
      close();
      await onRefresh();
    }
    return ok;
  };

  const runCancelPayout = async (reason: string) => {
    if (!creator) return false;
    const token = await getToken();
    if (!token) return false;
    const ok = await cancelPayout(creator._id, reason, token);
    if (ok) {
      close();
      await onRefresh();
    }
    return ok;
  };

  const amount = payoutState?.preview
    ? `${payoutState.preview.amount} ${payoutState.preview.currency}`
    : null;

  return (
    <Dialog open={!!phaseDialog && !!creator} onOpenChange={(o) => !o && close()}>
      <DialogContent className="md:min-w-2xl overflow-y-auto max-h-[85vh]">
        <DialogHeader>
          <DialogTitle>
            @{creator?.username} — {label}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Details and decisions for the {label} phase of @{creator?.username}.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {/* ── 1. Summary — identical shape for every phase ─────────── */}
          <Frame>
            <FrameHeader>
              <FrameTitle>Summary</FrameTitle>
            </FrameHeader>
            <FramePanel className="p-0">
              <div className="flex flex-col divide-y">
                <InfoRow label="State">
                  {isCompleted ? (
                    <span className="text-success flex items-center gap-1">
                      <CheckCircle2Icon className="size-3.5" /> Completed {fmtDate(completedAt) ?? ""}
                    </span>
                  ) : isCurrent && creator?.status ? (
                    <BadgeStatus
                      status={creator.status}
                      pipeline={pipeline}
                      size="sm"
                      showDescription={false}
                    />
                  ) : (
                    <span className="text-muted-foreground font-normal">Not reached yet</span>
                  )}
                </InfoRow>
                {isCurrent && phase?.entered_at && <InfoRow label="Entered">{fmtDate(phase.entered_at)}</InfoRow>}
                {isCurrent && phase?.sent_at && <InfoRow label="Last email">{fmtDate(phase.sent_at)}</InfoRow>}
                {isCurrent && shape !== "action" && shape !== "wait" && settings && (
                  <InfoRow label="Nudges">
                    {settings.nudgeEnabled ? (
                      <>
                        {phase?.nudge_count ?? 0} of {settings.maxNudges}
                      </>
                    ) : (
                      <span className="text-muted-foreground font-normal">off</span>
                    )}
                  </InfoRow>
                )}
                {shape === "review" && phaseKey !== "contract" && (phase?.revision_count ?? 0) > 0 && settings && (
                  <InfoRow label="Revisions">
                    {phase!.revision_count}
                    <span className="text-muted-foreground font-normal text-xs">
                      · hands off at {settings.handoffAfterRevisions}
                    </span>
                  </InfoRow>
                )}
                {shape === "review" && settings && (
                  <InfoRow label="Judged by">
                    {settings.judgeMode === "auto"
                      ? "Amy (auto)"
                      : settings.judgeMode === "assist"
                        ? "Amy assists — you approve"
                        : "The team"}
                  </InfoRow>
                )}
                {isCurrent && shape === "action" && (
                  <InfoRow label="Attempts">{phase?.action_attempts ?? 0} of 3</InfoRow>
                )}
              </div>
              {entry?.instructions && (
                <p className="border-t p-3 text-xs text-muted-foreground whitespace-pre-wrap">
                  <span className="font-mono uppercase text-[11px] block mb-0.5">Amy's instructions</span>
                  {entry.instructions}
                </p>
              )}
            </FramePanel>
          </Frame>

          {/* ── 2. Phase facts ───────────────────────────────────────── */}
          {phaseKey === "contract" && phase?.contract && (
            <Frame>
              <FrameHeader>
                <FrameTitle>Contract</FrameTitle>
              </FrameHeader>
              <FramePanel className="p-0">
                <ContractBlock contract={phase.contract} />
              </FramePanel>
            </Frame>
          )}

          {isPayoutPhase && payoutState && (
            <Frame>
              <FrameHeader>
                <FrameTitle>Payout</FrameTitle>
              </FrameHeader>
              <FramePanel className="p-0">
                <PaymentBlock payment={payoutState} />
              </FramePanel>
            </Frame>
          )}

          {shape === "wait" && (waitMode || gate) && (
            <Frame>
              <FrameHeader>
                <FrameTitle>Hold</FrameTitle>
              </FrameHeader>
              <FramePanel className="p-0">
                <div className="flex flex-col divide-y">
                  <InfoRow label="Released by">
                    {gate?.released_at ? (
                      <span className="text-success">
                        {gate.released_by === "human" ? "The team" : "Matching event"} · {fmtDate(gate.released_at)}
                      </span>
                    ) : entry?.manual ? (
                      "The team (manual hold)"
                    ) : entry?.trigger?.slug ? (
                      <span className="font-mono text-xs">{entry.trigger.slug}</span>
                    ) : (
                      "Matching event"
                    )}
                  </InfoRow>
                  {gate?.release_note && (
                    <InfoRow label="Release note">
                      <span className="font-normal">{gate.release_note}</span>
                    </InfoRow>
                  )}
                  {!gate?.released_at && gate?.stall_announced_at && (
                    <InfoRow label="Stalled">
                      <span className="text-warning flex items-center gap-1">
                        <TriangleAlertIcon className="size-3.5" /> flagged {fmtDate(gate.stall_announced_at)}
                      </span>
                    </InfoRow>
                  )}
                  {entry?.wait_message && (
                    <InfoRow label="Creator was told">
                      <span className="font-normal text-muted-foreground">“{entry.wait_message}”</span>
                    </InfoRow>
                  )}
                </div>
              </FramePanel>
            </Frame>
          )}

          {/* ── 3. Submissions — email-thread style, newest first ────── */}
          {artifacts.length > 0 && (
            <Frame>
              <FrameHeader>
                <FrameTitle>Submissions</FrameTitle>
              </FrameHeader>
              <FramePanel className="p-0">
                {artifacts.map((artifact, i) => (
                  <ArtifactThreadItem
                    key={`${artifact.at}-${i}`}
                    artifact={artifact}
                    // Label by the artifact's OWN kind — the no-slice fallback
                    // can surface artifacts from other phases.
                    label={phaseDisplayLabel(pipeline, artifact.kind)}
                    username={creator?.username}
                    avatar={creator?.avatar}
                  />
                ))}
              </FramePanel>
            </Frame>
          )}

          {/* ── 4. Tool runs (custom phases) ─────────────────────────── */}
          {checks.length > 0 && (
            <Frame>
              <FrameHeader>
                <FrameTitle>Tool runs</FrameTitle>
              </FrameHeader>
              <FramePanel className="p-0">
                <div className="flex flex-col divide-y">
                  {checks.map((c, i) => (
                    <InfoRow key={`${c.at}-${i}`} label={fmtDate(c.at) ?? ""}>
                      {c.ok ? (
                        <CheckCircle2Icon className="size-3.5 text-success shrink-0" />
                      ) : (
                        <TriangleAlertIcon className="size-3.5 text-warning shrink-0" />
                      )}
                      <span className="font-mono text-xs">{c.action_slug}</span>
                      <span className="font-normal text-muted-foreground">{c.note}</span>
                    </InfoRow>
                  ))}
                </div>
              </FramePanel>
            </Frame>
          )}

          {/* ── 5. Decision — handoff-style verdict frame ────────────── */}
          {verdictMode && (
            <Frame>
              <FrameHeader>
                <FrameTitle>Verdict</FrameTitle>
              </FrameHeader>
              <FramePanel className="p-0">
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  maxLength={2000}
                  placeholder="Feedback for the creator."
                  className="min-h-24 resize-none pb-12 focus-visible:ring-indigo-500/20 focus-visible:border-indigo-500"
                />
                <div className="absolute bottom-2 right-2 flex gap-2">
                  <Button
                    variant="warning-ghost"
                    size="sm"
                    disabled={isLoading || !notes.trim()}
                    onClick={() => runVerdict("revision")}
                  >
                    {isLoading ? <Spinner /> : <UndoIcon />} Request revision
                  </Button>
                  <Button variant="neutral" size="sm" disabled={isLoading} onClick={() => runVerdict("approve")}>
                    {isLoading ? <Spinner /> : <CheckCircle2Icon />} Approve
                  </Button>
                </div>
              </FramePanel>
              <FrameFooter className="flex items-center justify-between gap-2">
                <span>Feedback is required to request a revision.</span>
                <span
                  onClick={() =>
                    openAmy(
                      `Let's discuss the ${label} phase for @${creator?.username} on campaign "${campaign?.campaign_name}" (campaign_creator_id ${creator?._id}, campaign_id ${campaign?._id}).`
                    )
                  }
                  className="text-foreground underline cursor-pointer hover:decoration-2 underline-offset-2"
                >
                  Discuss with Amy
                </span>
              </FrameFooter>
            </Frame>
          )}

          {payoutMode && (
            <Frame>
              <FrameHeader>
                <FrameTitle>Decision</FrameTitle>
              </FrameHeader>
              <FramePanel className="flex w-full gap-2 p-3">
                <PayoutDecision
                  username={creator?.username}
                  avatar={creator?.avatar}
                  amount={amount}
                  isLoading={isLoading}
                  onConfirm={runConfirmPayout}
                  onCancel={runCancelPayout}
                />
              </FramePanel>
            </Frame>
          )}

          {/* ── 6. Escape hatch — every phase (verdict mode carries its
                 own Discuss link in the frame footer) ─────────────────── */}
          {!verdictMode && (
            <div className="flex justify-end">
              <Button
                variant="neutral"
                size="sm"
                onClick={() =>
                  openAmy(
                    waitMode
                      ? `Tell me about the "${label}" hold for @${creator?.username} on campaign "${campaign?.campaign_name}" (campaign_creator_id ${creator?._id}, campaign_id ${campaign?._id}) — I may want to release it.`
                      : `Let's discuss the ${label} phase for @${creator?.username} on campaign "${campaign?.campaign_name}" (campaign_creator_id ${creator?._id}, campaign_id ${campaign?._id}).`
                  )
                }
              >
                <MessageCircleQuestionIcon /> {waitMode ? "Release with Amy" : "Discuss with Amy"}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
